import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Modal, Button } from '../../../components';
import { qualityService } from '../../../services';
import { Badge, FieldLabel, Notice, inputClass, errorMessage } from './shared';

/**
 * Manage the library's category folders.
 *
 * Standard categories are fixed — they are the same for every organisation, so
 * a service that renames or deletes one would file its policies somewhere
 * nobody else recognises. An organisation's own additions can be removed, but
 * only while nothing points at them: a category used by ANY revision,
 * including a superseded one, would leave that old version filed under a
 * folder that no longer exists.
 */
const CategoriesModal = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await qualityService.getPolicyCategories();
      setRows(response.data || []);
    } catch (err) {
      setError(errorMessage(err, 'Categories could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { setName(''); setError(''); load(); } }, [open, load]);

  if (!open) return null;

  const add = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await qualityService.createPolicyCategory(name.trim());
      toast.success('Category added. It is now available when adding or editing a policy.');
      setName('');
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await qualityService.deletePolicyCategory(row.id);
      toast.success('Empty category removed.');
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Manage categories" maxWidth="max-w-xl">
      <form onSubmit={add} className="space-y-4">
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 -mt-1">
          Use the standard categories first. Add one of your own only when it helps staff find policies.
        </p>

        {error && <Notice tone="red">{error}</Notice>}

        <div>
          <FieldLabel>New category name</FieldLabel>
          <div className="flex gap-2.5">
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder="e.g. Specialist dementia support"
              autoComplete="off"
            />
            <Button variant="primary" type="submit" loading={saving} disabled={!name.trim()}>Add</Button>
          </div>
        </div>

        <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 leading-relaxed">
          These are library folders, not a regulatory checklist. A category used by a policy — current,
          draft, archived or a previous version — cannot be removed.
        </p>

        <div className="border-t border-[#E3E9F3] dark:border-gray-800 max-h-[360px] overflow-auto">
          {loading ? (
            <p className="py-8 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading…</p>
          ) : rows.map((row) => (
            <div key={row.name} className="flex items-center justify-between gap-4 py-3 border-b border-[#EEF2F8] dark:border-gray-800">
              <div className="min-w-0">
                <strong className="block text-[13px] font-semibold text-[#0B1220] dark:text-gray-100 truncate">{row.name}</strong>
                <span className="block text-[11px] text-[#94A3B8] dark:text-gray-500 mt-1">
                  {row.standard ? 'Standard category' : 'Custom category'} · used by {row.usage} {row.usage === 1 ? 'version' : 'versions'}
                </span>
              </div>
              {row.standard ? (
                <Badge>Standard</Badge>
              ) : (
                <Button
                  size="sm"
                  variant="danger-soft"
                  type="button"
                  disabled={row.usage > 0}
                  title={row.usage > 0 ? 'Used by a current, draft, archived or previous policy version' : undefined}
                  onClick={() => remove(row)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-1">
          <Button variant="secondary" type="button" onClick={onClose}>Done</Button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoriesModal;
